import type { PaymentPeriod, Resident } from '../types';

export interface OpenCicilanSisa {
  cicilan1: PaymentPeriod;
  sisaDueDate: string;
  periodDueDate: string;
  isOverdue: boolean;
  daysSince: number;
}

/** Add months to YYYY-MM-DD using UTC, with month-end clamping. */
export function addMonthsToDate(dateStr: string, months: number): string {
  if (!dateStr || !months) return '';
  const base = new Date(dateStr + 'T00:00:00.000Z');
  if (Number.isNaN(base.getTime())) return '';

  const day = base.getUTCDate();
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();

  const target = new Date(Date.UTC(year, month + months, day));
  if (target.getUTCDate() !== day) {
    // Overflow (e.g. Jan 31 + 1 month) → last day of previous month
    target.setUTCDate(0);
  }
  return target.toISOString().split('T')[0];
}

function periodSortKey(period: PaymentPeriod): string {
  // Prefer proof date; fall back to nextPaymentDate / id for stable ordering
  return period.paymentProofDate || period.nextPaymentDate || period.id || '';
}

/**
 * Detect open cicilan remainder for a resident.
 * Open if latest Cicilan ke-1 has no later Cicilan ke-2+ and no later Pelunasan.
 */
export function getOpenCicilanSisa(resident: Resident, today = new Date()): OpenCicilanSisa | null {
  const periods = resident?.paymentPeriods;
  if (!periods || periods.length === 0) return null;

  const sorted = [...periods].sort((a, b) =>
    periodSortKey(a).localeCompare(periodSortKey(b))
  );

  const cicilan1List = sorted.filter(
    (p) => p.category === 'Cicilan' && Number(p.installmentNumber || 0) === 1
  );
  if (cicilan1List.length === 0) return null;

  const cicilan1 = cicilan1List[cicilan1List.length - 1];
  const cicilan1Index = sorted.findIndex((p) => p.id === cicilan1.id);
  const afterCicilan1 = cicilan1Index >= 0 ? sorted.slice(cicilan1Index + 1) : [];

  const hasCloserAfter = afterCicilan1.some((p) => {
    if (p.category === 'Pelunasan') return true;
    if (p.category === 'Cicilan' && Number(p.installmentNumber || 0) >= 2) return true;
    return false;
  });

  if (hasCloserAfter) return null;

  const proofDate = cicilan1.paymentProofDate || cicilan1.startDate || '';
  if (!proofDate) return null;

  const sisaDueDate = addMonthsToDate(proofDate, 1);
  if (!sisaDueDate) return null;

  const periodDueDate =
    cicilan1.nextPaymentDate ||
    resident.nextPaymentDate ||
    '';

  const todayMid = new Date(today);
  todayMid.setHours(0, 0, 0, 0);
  const sisaDue = new Date(sisaDueDate + 'T00:00:00');
  sisaDue.setHours(0, 0, 0, 0);

  const diffMs = todayMid.getTime() - sisaDue.getTime();
  const daysSince = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const isOverdue = daysSince > 0;

  return {
    cicilan1,
    sisaDueDate,
    periodDueDate,
    isOverdue,
    // For panel "hari": show days since sisa due if overdue, else days since cicilan1 (like DP panel spirit)
    daysSince: isOverdue ? daysSince : Math.max(0, Math.floor(
      (todayMid.getTime() - new Date(proofDate + 'T00:00:00').getTime()) / (1000 * 60 * 60 * 24)
    )),
  };
}

export function isCicilanSisaOpen(resident: Resident, today = new Date()): boolean {
  return getOpenCicilanSisa(resident, today) !== null;
}

/** Suggested next installment number for Transaksi form. */
export function suggestedCicilanNumber(resident: Resident | undefined): number {
  if (!resident) return 1;
  return isCicilanSisaOpen(resident) ? 2 : 1;
}
